/**
 * Password validation utilities following NIST guidelines
 * NIST recommends: length > complexity, check common passwords, provide feedback
 */

// Common weak passwords to check against
const COMMON_PASSWORDS = [
  'password', 'password123', '12345678', '12345678', 'qwerty', 'abc123',
  'monkey', '1234567', 'letmein', 'trustno1', 'dragon', 'baseball',
  'iloveyou', 'master', 'sunshine', 'ashley', 'bailey', 'passw0rd',
  'shadow', '123123', '654321', 'superman', 'qazwsx', 'michael',
  'football', 'welcome', 'jesus', 'ninja', 'mustang', 'password1'
];

/**
 * Check if password is in common passwords list
 */
export const isCommonPassword = (password) => {
  const lowerPassword = password.toLowerCase();
  return COMMON_PASSWORDS.some(common => lowerPassword.includes(common) || common.includes(lowerPassword));
};

/**
 * Calculate password strength score (0-100)
 * Based on length, variety, and common password check
 */
export const calculatePasswordStrength = (password) => {
  if (!password) return 0;
  
  let score = 0;
  
  // Length scoring (most important per NIST)
  if (password.length >= 8) score += 20;
  if (password.length >= 12) score += 20;
  if (password.length >= 16) score += 20;
  if (password.length >= 20) score += 10;
  
  // Variety scoring (bonus, not required)
  if (/[a-z]/.test(password)) score += 5;
  if (/[A-Z]/.test(password)) score += 5;
  if (/[0-9]/.test(password)) score += 5;
  if (/[^a-zA-Z0-9]/.test(password)) score += 5;
  
  // Penalty for common passwords
  if (isCommonPassword(password)) {
    score = Math.max(0, score - 30);
  }
  
  // Penalty for repetitive patterns
  if (/(.)\1{3,}/.test(password)) {
    score = Math.max(0, score - 10);
  }
  
  // Penalty for sequential patterns
  if (/12345|abcde|qwerty/i.test(password)) {
    score = Math.max(0, score - 10);
  }
  
  return Math.min(100, score);
};

/**
 * Get password strength label
 */
export const getPasswordStrengthLabel = (strength) => {
  if (strength < 30) return { label: 'Very Weak', color: '#F43F5E' }; // rose-500
  if (strength < 50) return { label: 'Weak', color: '#F97316' }; // orange-500
  if (strength < 70) return { label: 'Fair', color: '#F59E0B' }; // amber-500
  if (strength < 90) return { label: 'Good', color: '#10B981' }; // emerald-500
  return { label: 'Strong', color: '#059669' }; // emerald-600
};

/**
 * Validate password and return validation result
 */
export const validatePassword = (password, confirmPassword = null) => {
  const errors = [];
  const warnings = [];
  
  // Required checks
  if (!password) {
    errors.push('Password is required');
    return { valid: false, errors, warnings, strength: 0 };
  }
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters');
  }
  
  if (password.length > 128) {
    errors.push('Password must be less than 128 characters');
  }
  
  // Check against common passwords
  if (isCommonPassword(password)) {
    errors.push('This password is too common. Please choose a more unique password.');
  }
  
  // Confirm password match
  if (confirmPassword !== null && password !== confirmPassword) {
    errors.push('Passwords do not match');
  }
  
  // Warnings (not blocking)
  const strength = calculatePasswordStrength(password);
  if (strength < 50 && password.length >= 8) {
    warnings.push('Consider using a longer password or adding more variety for better security');
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings,
    strength
  };
};

/**
 * Get password recommendations based on current password
 */
export const getPasswordRecommendations = (password) => {
  const recommendations = [];
  
  if (!password) return recommendations;
  
  if (password.length < 12) {
    recommendations.push('Use at least 12 characters for better security');
  }
  
  if (!/[a-z]/.test(password) || !/[A-Z]/.test(password)) {
    recommendations.push('Mix uppercase and lowercase letters');
  }
  
  if (!/[0-9]/.test(password)) {
    recommendations.push('Include numbers');
  }
  
  if (!/[^a-zA-Z0-9]/.test(password)) {
    recommendations.push('Add special characters (!@#$% etc.)');
  }
  
  if (isCommonPassword(password)) {
    recommendations.push('Avoid common words and patterns');
  }
  
  return recommendations;
};




